import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, XCircle, CheckCircle, Clock, AlertTriangle } from "lucide-react";
import { PlanPill } from "../components/Badges";

const STATUS_LABEL = {
  pending: { label: "Aguardando autorização", color: "text-amber-700 bg-amber-50 border-amber-200", icon: Clock },
  authorized: { label: "ATIVA — débito automático", color: "text-emerald-700 bg-emerald-50 border-emerald-200", icon: CheckCircle },
  paused: { label: "Pausada", color: "text-orange-700 bg-orange-50 border-orange-200", icon: AlertTriangle },
  cancelled: { label: "Cancelada", color: "text-stone-500 bg-stone-50 border-stone-200", icon: XCircle },
};

export default function MerchantSubscriptions() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, sRes] = await Promise.all([
        api.get(`/companies/${companyId}`),
        api.get(`/subscriptions/by-company/${companyId}`),
      ]);
      setCompany(cRes.data);
      setSubs(sRes.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao carregar");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  const cancel = async (subId) => {
    if (!window.confirm("Cancelar essa assinatura recorrente? O cartão deixará de ser cobrado.")) return;
    setBusy(true);
    try {
      await api.post(`/subscriptions/${subId}/cancel`);
      toast.success("Assinatura cancelada");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <Link to="/merchant" className="inline-flex items-center text-sm text-blue-900 hover:text-blue-950 mb-4">
        <ArrowLeft size={14} className="mr-1"/> Voltar ao painel
      </Link>
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 mb-1">Assinaturas</h1>
      {company && <p className="text-stone-500">{company.name}</p>}

      <Card className="mt-6 p-5 rounded-2xl border-stone-200">
        {loading && <div className="py-10 text-center text-stone-500">Carregando...</div>}
        {!loading && subs.length === 0 && (
          <div className="py-10 text-center">
            <p className="text-stone-500 mb-4">Nenhuma assinatura recorrente ainda.</p>
            <Link to="/merchant" className="text-blue-900 font-bold hover:underline" data-testid="back-to-dashboard">
              Voltar e escolher um plano →
            </Link>
          </div>
        )}
        {!loading && subs.length > 0 && (
          <div className="space-y-3">
            {subs.map((s) => {
              const st = STATUS_LABEL[s.status] || STATUS_LABEL.pending;
              const Icon = st.icon;
              return (
                <div key={s.id} className={`p-4 rounded-xl border ${st.color}`} data-testid={`sub-row-${s.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <Icon size={22}/>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <PlanPill plan={s.plan_tier}/>
                          <span className="font-bold text-sm">R$ {(s.amount || 0).toFixed(2)}/mês</span>
                        </div>
                        <p className="text-xs mt-1 font-semibold">{st.label}</p>
                        <p className="text-[11px] mt-1 opacity-75">
                          Criada em {new Date(s.created_at).toLocaleDateString("pt-BR")}
                          {s.last_payment_at && ` · Último débito: ${new Date(s.last_payment_at).toLocaleDateString("pt-BR")}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {s.status === "pending" && s.init_point && (
                        <a href={s.init_point} className="inline-flex items-center text-xs px-3 py-2 rounded-lg bg-blue-900 text-white hover:bg-blue-950" data-testid={`sub-authorize-${s.id}`}>
                          <RefreshCw size={13} className="mr-1"/> Autorizar agora
                        </a>
                      )}
                      {(s.status === "pending" || s.status === "authorized" || s.status === "paused") && (
                        <Button size="sm" variant="outline" onClick={() => cancel(s.id)} disabled={busy} className="rounded-lg text-red-700 border-red-200 hover:bg-red-50" data-testid={`sub-cancel-${s.id}`}>
                          <XCircle size={13} className="mr-1"/> Cancelar
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card className="mt-4 p-4 rounded-2xl border-blue-200 bg-blue-50 text-xs text-blue-900">
        <strong>Como funciona?</strong> A assinatura recorrente usa o sistema de débito automático do Mercado Pago.
        Após autorizar, seu cartão de crédito será cobrado todo mês automaticamente e o plano renovado sem ação manual.
        Você pode cancelar a qualquer momento — o cartão deixa de ser cobrado mas o plano permanece ativo até o fim do mês pago.
      </Card>
    </div>
  );
}
